import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import ReactQuill from "react-quill";
import { ToastContainer, toast, Zoom } from "react-toastify";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";
import baseURL from "../../Services/Url";
import FirstNavbar from "../dashboard/FirstNavbar";
import SideBar from "../dashboard/SideBar";
import {
  createQuillModules,
  quillFormats,
  uploadQuillImage,
} from "../Components/quillConfig";

const slugify = (value) =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const stripHtml = (value = "") => value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const splitLines = (value = "") =>
  value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

const splitCsv = (value = "") =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const orderedItems = (value) =>
  Array.isArray(value)
    ? [...value].sort(
        (left, right) =>
          Number(left?.order || Number.MAX_SAFE_INTEGER) -
          Number(right?.order || Number.MAX_SAFE_INTEGER)
      )
    : [];

const emptyCard = () => ({
  title: "",
  description: "",
  items: "",
  accentColor: "",
  linkText: "",
  linkUrl: "",
});

const emptySection = () => ({
  title: "",
  subtitle: "",
  paragraphs: "",
  quote: "",
  bullets: "",
  cards: [],
});

const createEmptyForm = (fields) =>
  fields.reduce((acc, field) => {
    if (["faqs", "sections", "cards", "coreValues"].includes(field.type)) {
      acc[field.name] = [];
    } else if (field.type === "intro") {
      acc[field.name] = { heading: "", paragraphs: "", highlightedText: "" };
    } else {
      acc[field.name] = field.defaultValue || "";
    }
    return acc;
  }, {});

const toFormValue = (value, field) => {
  if (field.type === "intro") {
    return {
      heading: value?.heading || "",
      paragraphs: Array.isArray(value?.paragraphs)
        ? value.paragraphs.join("\n")
        : "",
      highlightedText: value?.highlightedText || "",
    };
  }
  if (field.type === "sections") {
    return orderedItems(value).map((section) => ({
      title: section.title || "",
      subtitle: section.subtitle || "",
      paragraphs: Array.isArray(section.paragraphs)
        ? section.paragraphs.join("\n")
        : "",
      quote: section.quote || "",
      bullets: Array.isArray(section.bullets) ? section.bullets.join("\n") : "",
      cards: orderedItems(section.cards).map((card) => ({
        ...emptyCard(),
        ...card,
        items: Array.isArray(card.items) ? card.items.join("\n") : "",
      })),
    }));
  }
  if (field.type === "cards") {
    return orderedItems(value).map((card) => ({
      ...emptyCard(),
      ...card,
      items: Array.isArray(card.items) ? card.items.join("\n") : "",
    }));
  }
  if (field.type === "faqs") {
    return orderedItems(value)
      .map((faq, index) => ({
          question: faq.question || "",
          answer: faq.answer || "",
          order: index + 1,
        }));
  }
  if (field.type === "coreValues") {
    return orderedItems(value).map((item, index) => ({
      name: item?.name || "",
      description: item?.description || "",
      order: index + 1,
    }));
  }
  if (field.type === "list") return Array.isArray(value) ? value.join("\n") : "";
  if (field.type === "keywords") return Array.isArray(value) ? value.join(", ") : value || "";
  return value || "";
};

const toPayloadValue = (value, field) => {
  if (field.type === "intro") {
    return {
      heading: (value?.heading || "").trim(),
      paragraphs: splitLines(value?.paragraphs || ""),
      highlightedText: (value?.highlightedText || "").trim(),
    };
  }
  if (field.type === "sections") {
    return (Array.isArray(value) ? value : []).map((section, index) => ({
      title: (section.title || "").trim(),
      subtitle: (section.subtitle || "").trim(),
      paragraphs: splitLines(section.paragraphs || ""),
      quote: (section.quote || "").trim(),
      bullets: splitLines(section.bullets || ""),
      cards: (Array.isArray(section.cards) ? section.cards : []).map(
        (card, cardIndex) => ({
          title: (card.title || "").trim(),
          description: (card.description || "").trim(),
          items: splitLines(card.items || ""),
          accentColor: (card.accentColor || "").trim(),
          linkText: (card.linkText || "").trim(),
          linkUrl: (card.linkUrl || "").trim(),
          order: cardIndex + 1,
        })
      ),
      order: index + 1,
    }));
  }
  if (field.type === "cards") {
    return (Array.isArray(value) ? value : []).map((card, index) => ({
      title: (card.title || "").trim(),
      description: (card.description || "").trim(),
      items: splitLines(card.items || ""),
      accentColor: (card.accentColor || "").trim(),
      linkText: (card.linkText || "").trim(),
      linkUrl: (card.linkUrl || "").trim(),
      order: index + 1,
    }));
  }
  if (field.type === "faqs") {
    return (Array.isArray(value) ? value : [])
      .map((faq, index) => ({
        question: (faq.question || "").trim(),
        answer: (faq.answer || "").trim(),
        order: index + 1,
      }))
      .filter((faq) => faq.question || faq.answer);
  }
  if (field.type === "coreValues") {
    return (Array.isArray(value) ? value : [])
      .map((item, index) => ({
        name: (item?.name || "").trim(),
        description: (item?.description || "").trim(),
        order: index + 1,
      }))
      .filter((item) => item.name || item.description);
  }
  if (field.type === "list") return splitLines(value);
  if (field.type === "keywords") return splitCsv(value);
  if (field.type === "number") return value === "" ? undefined : Number(value);
  return value;
};

function CmsManager({
  title,
  resource,
  fields,
  singleton = false,
  supportsReorder = false,
}) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(createEmptyForm(fields));
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(singleton);
  const [deleteItem, setDeleteItem] = useState(null);
  const [loader, setLoader] = useState(false);
  const [uploadingField, setUploadingField] = useState("");

  const listFields = useMemo(
    () => fields.filter((field) => field.showInGrid !== false),
    [fields]
  );

  const quillModules = useMemo(
    () =>
      createQuillModules(function imageHandler() {
        uploadQuillImage(this.quill, {
          folder: `${resource}/content`,
          entityType: resource,
          onError: (err) => {
            toast.error(
              err.response?.data?.message || "Unable to upload content image"
            );
          },
        });
      }),
    [resource]
  );

  const buildForm = useCallback((item = {}) => {
    const nextForm = createEmptyForm(fields);
    fields.forEach((field) => {
      nextForm[field.name] = toFormValue(item[field.name], field);
    });
    return nextForm;
  }, [fields]);

  const fetchItems = useCallback(() => {
    setLoader(true);
    axios
      .get(`${baseURL}/${resource}`)
      .then((res) => {
        if (singleton) {
          const item = res.data || {};
          setItems(item.id ? [item] : []);
          setEditingItem(item.id ? item : null);
          setForm(buildForm(item));
          setShowForm(true);
          return;
        }

        setItems(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || `Unable to load ${title}`);
      })
      .finally(() => setLoader(false));
  }, [buildForm, resource, singleton, title]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const resetForm = () => {
    setForm(createEmptyForm(fields));
    setEditingItem(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm(buildForm(item));
    setShowForm(true);
  };

  const updateField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "title" && !prev.slug ? { slug: slugify(value) } : {}),
      ...(name === "name" && !prev.slug ? { slug: slugify(value) } : {}),
      ...(name === "title" && !prev.metaTitle ? { metaTitle: value } : {}),
      ...(name === "name" && !prev.metaTitle ? { metaTitle: value } : {}),
    }));
  };

  const addFaq = (fieldName) => {
    updateField(fieldName, [
      ...(Array.isArray(form[fieldName]) ? form[fieldName] : []),
      { question: "", answer: "" },
    ]);
  };

  const updateFaq = (fieldName, index, key, value) => {
    const faqs = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    faqs[index] = { ...faqs[index], [key]: value };
    updateField(fieldName, faqs);
  };

  const removeFaq = (fieldName, index) => {
    updateField(
      fieldName,
      (Array.isArray(form[fieldName]) ? form[fieldName] : []).filter(
        (_, faqIndex) => faqIndex !== index
      )
    );
  };

  const moveFaq = (fieldName, index, direction) => {
    const faqs = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= faqs.length) return;
    [faqs[index], faqs[targetIndex]] = [faqs[targetIndex], faqs[index]];
    updateField(fieldName, faqs);
  };

  const updateIntro = (fieldName, key, value) => {
    updateField(fieldName, { ...(form[fieldName] || {}), [key]: value });
  };

  const addListItem = (fieldName, item) => {
    updateField(fieldName, [
      ...(Array.isArray(form[fieldName]) ? form[fieldName] : []),
      item,
    ]);
  };

  const updateListItem = (fieldName, index, key, value) => {
    const items = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    items[index] = { ...items[index], [key]: value };
    updateField(fieldName, items);
  };

  const removeListItem = (fieldName, index) => {
    updateField(
      fieldName,
      (Array.isArray(form[fieldName]) ? form[fieldName] : []).filter(
        (_, itemIndex) => itemIndex !== index
      )
    );
  };

  const moveListItem = (fieldName, index, direction) => {
    const items = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
    updateField(fieldName, items);
  };

  const addSectionCard = (fieldName, sectionIndex) => {
    const sections = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    const section = sections[sectionIndex] || emptySection();
    sections[sectionIndex] = {
      ...section,
      cards: [...(Array.isArray(section.cards) ? section.cards : []), emptyCard()],
    };
    updateField(fieldName, sections);
  };

  const updateSectionCard = (fieldName, sectionIndex, cardIndex, key, value) => {
    const sections = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    const cards = [...(Array.isArray(sections[sectionIndex]?.cards) ? sections[sectionIndex].cards : [])];
    cards[cardIndex] = { ...cards[cardIndex], [key]: value };
    sections[sectionIndex] = { ...sections[sectionIndex], cards };
    updateField(fieldName, sections);
  };

  const removeSectionCard = (fieldName, sectionIndex, cardIndex) => {
    const sections = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    sections[sectionIndex] = {
      ...sections[sectionIndex],
      cards: (sections[sectionIndex]?.cards || []).filter(
        (_, index) => index !== cardIndex
      ),
    };
    updateField(fieldName, sections);
  };

  const moveSectionCard = (fieldName, sectionIndex, cardIndex, direction) => {
    const sections = [...(Array.isArray(form[fieldName]) ? form[fieldName] : [])];
    const cards = [...(Array.isArray(sections[sectionIndex]?.cards) ? sections[sectionIndex].cards : [])];
    const targetIndex = cardIndex + direction;
    if (targetIndex < 0 || targetIndex >= cards.length) return;
    [cards[cardIndex], cards[targetIndex]] = [cards[targetIndex], cards[cardIndex]];
    sections[sectionIndex] = { ...sections[sectionIndex], cards };
    updateField(fieldName, sections);
  };

  const uploadImage = (field, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", resource);
    formData.append("entityType", resource);
    formData.append("altText", form[`${field.name}Alt`] || form.altText || "");

    setUploadingField(field.name);
    axios
      .post(`${baseURL}/media/upload`, formData)
      .then((res) => {
        const url = res.data?.media?.url || res.data?.upload?.Location;
        if (url) updateField(field.name, url);
        toast.success("Image uploaded");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to upload image");
      })
      .finally(() => setUploadingField(""));
  };

  const buildPayload = () => {
    const payload = {};
    fields.forEach((field) => {
      const value = toPayloadValue(form[field.name], field);
      if (value !== undefined) payload[field.name] = value;
    });

    if (payload.slug) payload.slug = slugify(payload.slug);
    return payload;
  };

  const saveItem = () => {
    const payload = buildPayload();
    const missingField = fields.find(
      (field) => field.required && !String(payload[field.name] || "").trim()
    );
    if (missingField) {
      toast.error(`${missingField.label} is required`);
      return;
    }

    setLoader(true);
    const method = editingItem?.id ? "PUT" : "POST";
    const url =
      !singleton && editingItem?.id
        ? `${baseURL}/${resource}/${editingItem.id}`
        : `${baseURL}/${resource}`;

    axios({ url, method, data: payload })
      .then(() => {
        toast.success(`${title} saved`);
        if (!singleton) {
          setShowForm(false);
          resetForm();
        }
        fetchItems();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || `Unable to save ${title}`);
      })
      .finally(() => setLoader(false));
  };

  const confirmDelete = () => {
    if (!deleteItem?.id) return;
    setLoader(true);
    axios
      .delete(`${baseURL}/${resource}/${deleteItem.id}`)
      .then(() => {
        toast.success(`${title} deleted`);
        setDeleteItem(null);
        fetchItems();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || `Unable to delete ${title}`);
      })
      .finally(() => setLoader(false));
  };

  const reorderItem = (item, direction) => {
    const index = items.findIndex((current) => current.id === item.id);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const reordered = [...items];
    const [movingItem] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, movingItem);
    const payload = reordered.map((current, currentIndex) => ({
      id: current.id,
      order: currentIndex + 1,
    }));

    setItems(reordered.map((current, currentIndex) => ({ ...current, order: currentIndex + 1 })));
    axios
      .put(`${baseURL}/${resource}/reorder`, { items: payload })
      .then((res) => {
        if (Array.isArray(res.data)) setItems(res.data);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Unable to reorder items");
        fetchItems();
      });
  };

  const columnDefs = [
    {
      headerName: "Id",
      valueGetter: "node.rowIndex + 1",
      maxWidth: 90,
      filter: false,
    },
    ...listFields.map((field) => ({
      headerName: field.label,
      field: field.name,
      minWidth: field.minWidth || 150,
      cellRendererFramework: (params) => {
        const value = params.data[field.name];
        if (field.type === "image") {
          return value ? (
            <img
              src={value}
              alt={params.data.altText || params.data.name || params.data.title || field.label}
              style={{ width: 52, height: 52, objectFit: field.objectFit || "cover" }}
            />
          ) : null;
        }
        if (field.type === "richtext") return stripHtml(value).slice(0, 120);
        if (Array.isArray(value)) return value.join(", ");
        return value || "";
      },
    })),
    ...(supportsReorder
      ? [
          {
            headerName: "Order",
            minWidth: 150,
            filter: false,
            cellRendererFramework: (params) => (
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  onClick={() => reorderItem(params.data, -1)}
                >
                  Up
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  type="button"
                  onClick={() => reorderItem(params.data, 1)}
                >
                  Down
                </Button>
              </div>
            ),
          },
        ]
      : []),
    {
      headerName: "Update",
      minWidth: 120,
      filter: false,
      cellRendererFramework: (params) => (
        <Button
          variant="outline-primary"
          size="sm"
          type="button"
          onClick={() => openEdit(params.data)}
        >
          Edit
        </Button>
      ),
    },
    {
      headerName: "Delete",
      minWidth: 120,
      filter: false,
      cellRendererFramework: (params) => (
        <Button
          variant="outline-danger"
          size="sm"
          type="button"
          onClick={() => setDeleteItem(params.data)}
        >
          Delete
        </Button>
      ),
    },
  ];

  const renderCardInputs = ({ card, onChange }) => (
    <Row>
      <Col md={6}>
        <Form.Control
          value={card.title || ""}
          onChange={(event) => onChange("title", event.target.value)}
          placeholder="Card title"
          className="mb-2"
        />
      </Col>
      <Col md={6}>
        <Form.Control
          value={card.accentColor || ""}
          onChange={(event) => onChange("accentColor", event.target.value)}
          placeholder="Accent colour, e.g. #0f766e"
          className="mb-2"
        />
      </Col>
      <Col md={12}>
        <Form.Control
          as="textarea"
          rows={2}
          value={card.description || ""}
          onChange={(event) => onChange("description", event.target.value)}
          placeholder="Card description"
          className="mb-2"
        />
      </Col>
      <Col md={12}>
        <Form.Control
          as="textarea"
          rows={3}
          value={card.items || ""}
          onChange={(event) => onChange("items", event.target.value)}
          placeholder="Card list items — one per line"
          className="mb-2"
        />
      </Col>
      <Col md={6}>
        <Form.Control
          value={card.linkText || ""}
          onChange={(event) => onChange("linkText", event.target.value)}
          placeholder="Link text"
          className="mb-2"
        />
      </Col>
      <Col md={6}>
        <Form.Control
          value={card.linkUrl || ""}
          onChange={(event) => onChange("linkUrl", event.target.value)}
          placeholder="Link URL"
          className="mb-2"
        />
      </Col>
    </Row>
  );

  const renderField = (field) => {
    if (field.type === "coreValues") {
      const values = Array.isArray(form[field.name]) ? form[field.name] : [];
      return (
        <Form.Group className="mb-3" key={field.name}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="mb-0">{field.label}</Form.Label>
            <Button
              variant="outline-success"
              size="sm"
              type="button"
              onClick={() =>
                addListItem(field.name, { name: "", description: "" })
              }
            >
              Add Core Value
            </Button>
          </div>
          {values.length === 0 && (
            <div className="text-muted border rounded p-3">
              No core values added yet.
            </div>
          )}
          {values.map((item, index) => (
            <div className="border rounded p-3 mb-3" key={`${field.name}-${index}`}>
              <div className="d-flex justify-content-between mb-2">
                <strong>Core Value {index + 1}</strong>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" type="button" disabled={index === 0} onClick={() => moveListItem(field.name, index, -1)}>Up</Button>
                  <Button size="sm" variant="outline-secondary" type="button" disabled={index === values.length - 1} onClick={() => moveListItem(field.name, index, 1)}>Down</Button>
                  <Button size="sm" variant="outline-danger" type="button" onClick={() => removeListItem(field.name, index)}>Remove</Button>
                </div>
              </div>
              <Form.Control
                value={item.name || ""}
                onChange={(event) =>
                  updateListItem(field.name, index, "name", event.target.value)
                }
                placeholder="Core value name"
                className="mb-2"
              />
              <Form.Control
                as="textarea"
                rows={3}
                value={item.description || ""}
                onChange={(event) =>
                  updateListItem(field.name, index, "description", event.target.value)
                }
                placeholder="Core value description"
              />
            </div>
          ))}
        </Form.Group>
      );
    }

    if (field.type === "intro") {
      const intro = form[field.name] || {};
      return (
        <Form.Group className="mb-3" key={field.name}>
          <Form.Label>{field.label}</Form.Label>
          <div className="border rounded p-3">
            <Form.Control
              value={intro.heading || ""}
              onChange={(event) =>
                updateIntro(field.name, "heading", event.target.value)
              }
              placeholder="Intro heading"
              className="mb-2"
            />
            <Form.Control
              as="textarea"
              rows={4}
              value={intro.paragraphs || ""}
              onChange={(event) =>
                updateIntro(field.name, "paragraphs", event.target.value)
              }
              placeholder="Intro paragraphs — one per line"
              className="mb-2"
            />
            <Form.Control
              as="textarea"
              rows={2}
              value={intro.highlightedText || ""}
              onChange={(event) =>
                updateIntro(field.name, "highlightedText", event.target.value)
              }
              placeholder="Highlighted text"
            />
          </div>
        </Form.Group>
      );
    }

    if (field.type === "cards") {
      const cards = Array.isArray(form[field.name]) ? form[field.name] : [];
      return (
        <Form.Group className="mb-3" key={field.name}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="mb-0">{field.label}</Form.Label>
            <Button
              variant="outline-success"
              size="sm"
              type="button"
              onClick={() => addListItem(field.name, emptyCard())}
            >
              Add Card
            </Button>
          </div>
          {cards.length === 0 && (
            <div className="text-muted border rounded p-3">No cards added yet.</div>
          )}
          {cards.map((card, index) => (
            <div className="border rounded p-3 mb-3" key={`${field.name}-${index}`}>
              <div className="d-flex justify-content-between mb-2">
                <strong>Card {index + 1}</strong>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" type="button" disabled={index === 0} onClick={() => moveListItem(field.name, index, -1)}>Up</Button>
                  <Button size="sm" variant="outline-secondary" type="button" disabled={index === cards.length - 1} onClick={() => moveListItem(field.name, index, 1)}>Down</Button>
                  <Button size="sm" variant="outline-danger" type="button" onClick={() => removeListItem(field.name, index)}>Remove</Button>
                </div>
              </div>
              {renderCardInputs({
                card,
                onChange: (key, value) =>
                  updateListItem(field.name, index, key, value),
              })}
            </div>
          ))}
        </Form.Group>
      );
    }

    if (field.type === "sections") {
      const sections = Array.isArray(form[field.name]) ? form[field.name] : [];
      return (
        <Form.Group className="mb-3" key={field.name}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="mb-0">{field.label}</Form.Label>
            <Button variant="outline-success" size="sm" type="button" onClick={() => addListItem(field.name, emptySection())}>
              Add Section
            </Button>
          </div>
          {sections.length === 0 && (
            <div className="text-muted border rounded p-3">No sections added yet.</div>
          )}
          {sections.map((section, sectionIndex) => (
            <div className="border border-primary rounded p-3 mb-4" key={`${field.name}-${sectionIndex}`}>
              <div className="d-flex justify-content-between mb-3">
                <strong>Section {sectionIndex + 1}</strong>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" type="button" disabled={sectionIndex === 0} onClick={() => moveListItem(field.name, sectionIndex, -1)}>Up</Button>
                  <Button size="sm" variant="outline-secondary" type="button" disabled={sectionIndex === sections.length - 1} onClick={() => moveListItem(field.name, sectionIndex, 1)}>Down</Button>
                  <Button size="sm" variant="outline-danger" type="button" onClick={() => removeListItem(field.name, sectionIndex)}>Remove</Button>
                </div>
              </div>
              <Row>
                <Col md={6}><Form.Control value={section.title || ""} onChange={(event) => updateListItem(field.name, sectionIndex, "title", event.target.value)} placeholder="Section title" className="mb-2" /></Col>
                <Col md={6}><Form.Control value={section.subtitle || ""} onChange={(event) => updateListItem(field.name, sectionIndex, "subtitle", event.target.value)} placeholder="Section subtitle" className="mb-2" /></Col>
                <Col md={12}><Form.Control as="textarea" rows={4} value={section.paragraphs || ""} onChange={(event) => updateListItem(field.name, sectionIndex, "paragraphs", event.target.value)} placeholder="Paragraphs — one per line" className="mb-2" /></Col>
                <Col md={12}><Form.Control as="textarea" rows={2} value={section.quote || ""} onChange={(event) => updateListItem(field.name, sectionIndex, "quote", event.target.value)} placeholder="Highlighted quote" className="mb-2" /></Col>
                <Col md={12}><Form.Control as="textarea" rows={4} value={section.bullets || ""} onChange={(event) => updateListItem(field.name, sectionIndex, "bullets", event.target.value)} placeholder="Bullet points — one per line" className="mb-3" /></Col>
              </Row>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Section cards</strong>
                <Button size="sm" variant="outline-success" type="button" onClick={() => addSectionCard(field.name, sectionIndex)}>Add Section Card</Button>
              </div>
              {(section.cards || []).map((card, cardIndex) => (
                <div className="bg-light border rounded p-3 mb-2" key={`section-${sectionIndex}-card-${cardIndex}`}>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Card {cardIndex + 1}</span>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-secondary" type="button" disabled={cardIndex === 0} onClick={() => moveSectionCard(field.name, sectionIndex, cardIndex, -1)}>Up</Button>
                      <Button size="sm" variant="outline-secondary" type="button" disabled={cardIndex === section.cards.length - 1} onClick={() => moveSectionCard(field.name, sectionIndex, cardIndex, 1)}>Down</Button>
                      <Button size="sm" variant="outline-danger" type="button" onClick={() => removeSectionCard(field.name, sectionIndex, cardIndex)}>Remove</Button>
                    </div>
                  </div>
                  {renderCardInputs({
                    card,
                    onChange: (key, value) =>
                      updateSectionCard(field.name, sectionIndex, cardIndex, key, value),
                  })}
                </div>
              ))}
            </div>
          ))}
        </Form.Group>
      );
    }

    if (field.type === "faqs") {
      const faqs = Array.isArray(form[field.name]) ? form[field.name] : [];
      return (
        <Form.Group className="mb-3" key={field.name}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="mb-0">{field.label}</Form.Label>
            <Button
              variant="outline-success"
              size="sm"
              type="button"
              onClick={() => addFaq(field.name)}
            >
              Add FAQ
            </Button>
          </div>
          {faqs.length === 0 && (
            <div className="text-muted border rounded p-3">
              No FAQs added yet.
            </div>
          )}
          {faqs.map((faq, index) => (
            <div className="border rounded p-3 mb-3" key={`${field.name}-${index}`}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <strong>FAQ {index + 1}</strong>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    disabled={index === 0}
                    onClick={() => moveFaq(field.name, index, -1)}
                  >
                    Up
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    disabled={index === faqs.length - 1}
                    onClick={() => moveFaq(field.name, index, 1)}
                  >
                    Down
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    type="button"
                    onClick={() => removeFaq(field.name, index)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
              <Form.Control
                type="text"
                value={faq.question || ""}
                onChange={(event) =>
                  updateFaq(field.name, index, "question", event.target.value)
                }
                placeholder="Question"
                className="mb-2"
              />
              <Form.Control
                as="textarea"
                rows={3}
                value={faq.answer || ""}
                onChange={(event) =>
                  updateFaq(field.name, index, "answer", event.target.value)
                }
                placeholder="Answer"
              />
            </div>
          ))}
        </Form.Group>
      );
    }

    if (field.type === "richtext") {
      return (
        <Form.Group className="mb-3" key={field.name}>
          <Form.Label>{field.label}</Form.Label>
          <ReactQuill
            theme="snow"
            value={form[field.name]}
            onChange={(value) => updateField(field.name, value)}
            modules={quillModules}
            formats={quillFormats}
          />
        </Form.Group>
      );
    }

    if (field.type === "textarea" || field.type === "list" || field.type === "keywords") {
      return (
        <Form.Group className="mb-3" key={field.name}>
          <Form.Label>{field.label}</Form.Label>
          <Form.Control
            as="textarea"
            rows={field.rows || 3}
            name={field.name}
            value={form[field.name]}
            onChange={(event) => updateField(field.name, event.target.value)}
            placeholder={field.placeholder}
          />
        </Form.Group>
      );
    }

    if (field.type === "select") {
      return (
        <Form.Group className="mb-3" key={field.name}>
          <Form.Label>{field.label}</Form.Label>
          <Form.Select
            value={form[field.name]}
            onChange={(event) => updateField(field.name, event.target.value)}
          >
            {(field.options || []).map((option) => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );
    }

    if (field.type === "image") {
      return (
        <Form.Group className="mb-3" key={field.name}>
          <Form.Label>{field.label}{field.required ? " *" : ""}</Form.Label>
          <Form.Control
            type="text"
            name={field.name}
            value={form[field.name]}
            onChange={(event) => updateField(field.name, event.target.value)}
            placeholder="Image URL"
            className="mb-2"
          />
          <Form.Control
            type="file"
            onChange={(event) => uploadImage(field, event.target.files[0])}
          />
          {uploadingField === field.name && (
            <Spinner animation="border" size="sm" className="mt-2" />
          )}
          {form[field.name] && (
            <img
              src={form[field.name]}
              alt={form.altText || field.label}
              className="mt-2"
              style={{
                width: 110,
                height: 76,
                objectFit: field.objectFit || "cover",
              }}
            />
          )}
        </Form.Group>
      );
    }

    return (
      <Form.Group className="mb-3" key={field.name}>
          <Form.Label>{field.label}{field.required ? " *" : ""}</Form.Label>
        <Form.Control
          type={field.type || "text"}
          name={field.name}
          value={form[field.name]}
          onChange={(event) => updateField(field.name, event.target.value)}
          placeholder={field.placeholder}
        />
      </Form.Group>
    );
  };

  return (
    <div>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar
        transition={Zoom}
        delay={500}
        limit={1}
      />
      <div className="sidebar">
        <SideBar />
      </div>

      <div className="content">
        <div className="container">
          <FirstNavbar />
          <div className="row my-2 align-items-center">
            <div className="col-md-6">
              <h3>
                <b>{title}</b>
              </h3>
            </div>
            {!singleton && (
              <div className="col-md-6 text-end">
                <Button variant="outline-success" type="button" onClick={openCreate}>
                  Add {title}
                </Button>
              </div>
            )}
          </div>

          {singleton ? (
            <div className="mt-4">
              <Form>
                <Row>
                  {fields.map((field) => (
                    <Col md={field.fullWidth === false ? 6 : 12} key={field.name}>
                      {renderField(field)}
                    </Col>
                  ))}
                </Row>
                <Button variant="primary" type="button" onClick={saveItem} disabled={loader}>
                  {loader ? <Spinner animation="border" size="sm" /> : "Save"}
                </Button>
              </Form>
            </div>
          ) : (
            <div className="ag-theme-alpine" style={{ height: "75vh", width: "100%" }}>
              <AgGridReact
                rowHeight={58}
                columnDefs={columnDefs}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  floatingFilter: true,
                  flex: 1,
                  resizable: true,
                  minWidth: 120,
                }}
                pagination={true}
                paginationPageSize={10}
                rowData={items}
              />
            </div>
          )}
        </div>
      </div>

      <Modal show={showForm && !singleton} onHide={() => setShowForm(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItem?.id ? "Update" : "Add"} {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              {fields.map((field) => (
                <Col md={field.fullWidth === false ? 6 : 12} key={field.name}>
                  {renderField(field)}
                </Col>
              ))}
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="button" onClick={saveItem} disabled={loader}>
            {loader ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deleteItem)} onHide={() => setDeleteItem(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this item?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={() => setDeleteItem(null)}>
            Cancel
          </Button>
          <Button variant="danger" type="button" onClick={confirmDelete} disabled={loader}>
            {loader ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CmsManager;
